import React from 'react';
import {
    Panel,
    PanelType,
    Stack,
    Text,
    DefaultButton,
    SearchBox,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { IReportingWidget, getWidgetDisplayName } from '../../../models/Dashboard';

interface IWidgetPickerPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    onAddWidget: (widget: IReportingWidget) => void;
    availableWidgets: IReportingWidget[];
    isLoading?: boolean;
}

const WidgetPickerPanel: React.FC<IWidgetPickerPanelProps> = ({ isOpen, onDismiss, onAddWidget, availableWidgets, isLoading }) => {
    const [filter, setFilter] = React.useState('');

    const filteredWidgets = availableWidgets.filter(w => {
        const name = getWidgetDisplayName(w.widgetType).toLowerCase();
        const desc = (w.description || '').toLowerCase();
        const f = filter.toLowerCase();
        return name.includes(f) || desc.includes(f);
    });

    const grouped: Record<string, IReportingWidget[]> = {};
    filteredWidgets.forEach(w => {
        const key = w.displayType || 'Other';
        (grouped[key] ||= []).push(w);
    });

    const groupTitles: Record<string, string> = {
        Metric: 'Statistics',
        BarChart: 'Bar Charts',
        LineChart: 'Line Charts',
        PieChart: 'Pie Charts',
        Table: 'Tables',
        Other: 'Other',
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Add Widget"
        >
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <SearchBox
                    placeholder="Search widgets..."
                    value={filter}
                    onChange={(_, newValue) => setFilter(newValue || '')}
                />

                {isLoading ? (
                    <Spinner size={SpinnerSize.medium} label="Loading widgets..." />
                ) : (
                    Object.keys(grouped).map(key => (
                        <WidgetGroup
                            key={key}
                            title={groupTitles[key] || key}
                            widgets={grouped[key]}
                            onAddWidget={onAddWidget}
                        />
                    ))
                )}

                {!isLoading && filteredWidgets.length === 0 && (
                    <Text variant="small" style={{ color: '#605e5c' }}>No widgets available.</Text>
                )}
            </Stack>
        </Panel>
    );
};

interface IWidgetGroupProps {
    title: string;
    widgets: IReportingWidget[];
    onAddWidget: (widget: IReportingWidget) => void;
}

const WidgetGroup: React.FC<IWidgetGroupProps> = ({ title, widgets, onAddWidget }) => (
    <Stack tokens={{ childrenGap: 8 }}>
        <Text variant="mediumPlus" style={{ fontWeight: 600 }}>{title}</Text>
        {widgets.map(widget => (
            <div
                key={widget.widgetType}
                style={{
                    border: '1px solid #edebe9',
                    borderRadius: 4,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div>
                    <Text variant="medium" style={{ fontWeight: 600, display: 'block' }}>{getWidgetDisplayName(widget.widgetType)}</Text>
                    <Text variant="small" style={{ color: '#605e5c' }}>{widget.description}</Text>
                </div>
                <DefaultButton
                    text="Add"
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => onAddWidget(widget)}
                />
            </div>
        ))}
    </Stack>
);

export default WidgetPickerPanel;
